import React, { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import {
    getMemberForImitating,
    getProfileByIdent,
    profileAtom,
} from '../../../api/profile/profile'
import {
    isAuthenticatedAtom,
    authRoles,
    canAccessLoenindplaceringer,
} from '../../../store/auth'
import { Profile } from '../../../models/Profile'

import { Department } from '../../../models/Department'

import breakpoints from '../../../theme/breakpoints'
import { colors } from '../../../theme/palette'

import { ISelectOptions, Select } from '../../ui/FormElememts/Select'
import { getDepartments } from '../../../api/departments'
import { Input } from '../../ui/FormElememts/Input'
import {
    Card,
    CardCell,
    CardHeader,
    CardHeaderCell,
    CardList,
    Circle,
    CircleText,
} from '../../ui/Card'
import { SortButton } from '../../ui/SortButton'
import { Typography } from '../../ui/Typography/Typography'
import { getInitials } from '../../../utils/initials'
import useSortMembersList from '../../../hooks/useSortMembersList'
import { Button } from '../../ui/Button/Button'

import { useContainerStyles } from '../../../theme/layout'
import { L } from '../../providers/Lokalise'
import { sortPlaceholder } from '../../../theme/reuseables'
import { FormControl } from '../../ui/FormElememts/FormControl'
import { Spinner } from '../../ui/Loaders/Spinner'
import { hasAccessToCase } from '../../../api/salary'

export type RegistrationFormFields = {
    fuldeNavn: string
    foedselsesdato: string
    telefon: string
    afdelingsnummer: string
}

export interface IMemberSuggestion {
    memberId: number
    fullName: string
    birthDay: string
    position: string
    role: string
}

const useStyles = createUseStyles({
    container: {
        padding: '0 24px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                padding: 0,
            },
        },
    },
    headerArea: {
        display: 'flex',
        padding: '.5rem 0',
        minHeight: '80px',
    },
    formArea: {
        display: 'grid',
        gridTemplateColumns: '3fr 3fr 1fr',
        gap: '20px',
        marginBottom: '50px',
    },
    topArea: {
        borderBottom: `1px solid ${colors.gray700}`,
        // TODO: remove this when this area has been developed.
        minHeight: '60px',
    },
    headerActions: {
        marginLeft: 'auto',
        flexShrink: 0,
    },
    members: {
        paddingTop: '20px',
    },
    form: {
        display: 'grid',
        gridTemplateColumns: '1fr 140px 140px 1fr 140px',
        gap: '10px',
    },
    linkButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0',
        margin: '0',
        borderRadius: '6px',
        boxSizing: 'border-box',
        textDecoration: 'none',
        fontWeight: 400,
        textAlign: 'center',
        transition: 'all 0.2s',
        boxShadow: 0,
        '&:hover': {
            cursor: 'pointer',
        },
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        border: 0,
        color: colors.green500,
        fontSize: '.875rem',
        textDecorationLine: 'underline',
    },
    subArea: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '1rem 160px 1rem 1rem',
        borderBottom: `1px solid ${colors.borderColor}`,
    },
    circleIcon: {
        width: '62px',
        height: '62px',
        borderRadius: '50%',
        border: `2px solid ${colors.white}`,
        backgroundColor: colors.blue300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '1rem',
    },
    sortPlaceholder: {
        ...sortPlaceholder,
    },
    content: {
        paddingTop: '1.875rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    messageWrapper: {
        marginTop: '2.25rem',
    },
    selectContainer: {
        position: 'relative',
    },
    selectTrigger: {
        position: 'relative',
        minHeight: '48px',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.labelColor,
    },
    selectContent: {
        overflow: 'hidden',
        backgroundColor: 'white',
        borderRadius: '6px',
        boxShadow:
            // eslint-disable-next-line max-len
            '0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)',
    },
    select: {
        position: 'relative',
        minHeight: '48px',
        backgroundColor: colors.white,
        border: `1px solid ${colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        fontSize: '1.125rem',
        paddingLeft: '.75rem',
        paddingRight: '.75rem',
        cursor: 'pointer',
        color: colors.labelColor,
    },
    hr: {
        opacity: '.4',
        margin: '0',
    },
})

const gridTemplateColumns = '10% 20% 20% 20% 15% 15%'

const Imitator: FC = () => {
    const [authProfile, _set] = useAtom(profileAtom)
    const [departmentNumber, _setDepartmentNumber] = useState('default')
    const [name, _setName] = useState<string | undefined>(undefined)
    const [suggestions, _setSuggestions] = useState<IMemberSuggestion[]>([])
    const [options, setOptions] = useState<ISelectOptions[]>([])
    const [loading, setLoading] = useState(false)
    const classes = useStyles()
    const { setSort, sortState } = useSortMembersList()
    const [, _setIsAuthenticated] = useAtom(isAuthenticatedAtom)
    const layout = useContainerStyles()
    const [roles, setAuthRoles] = useAtom(authRoles)
    const [, _setCanAccessLoenindplaceringer] = useAtom(
        canAccessLoenindplaceringer
    )
    const search = async (d: string, n: string): Promise<any> => {
        setLoading(true)
        const response = await getMemberForImitating(d, n)

        if (
            response.data &&
            response.data.Medlemmer &&
            response.data.Medlemmer.length
        ) {
            const result = response.data.Medlemmer.map((member: any) => {
                const splitted = member.FuldeNavn.split(',')
                // TR, FTR, TR-sup, FTR-sup., FTR-status
                const includedPositions = [
                    'TR',
                    'FTR',
                    'TR-sup',
                    'FTR-sup.',
                    'FTR-status',
                ]
                const positions = splitted
                    .slice(2)
                    .filter((pos) => includedPositions.includes(pos.trim()))
                    .join(', ')

                return {
                    memberId: member.Medlemsident,
                    fullName: splitted[0],
                    birthDay: splitted[1],
                    position: positions,
                    role: member.Rolle,
                }
            })

            _setSuggestions(result)
        }

        setLoading(false)
    }

    // useEffect(() => {
    //     if (name && name !== '' && departmentNumber) {
    //         search(departmentNumber, name)
    //     } else {
    //         _setSuggestions([])
    //     }
    // }, [departmentNumber, name])

    useEffect(() => {
        const getDeps = async (): Promise<boolean> => {
            if (
                authProfile.data &&
                authProfile.data.RolleListe.indexOf('Imitator') !== -1
            ) {
                const d: ISelectOptions[] = [
                    {
                        label: authProfile.data.Afdeling.Navn,
                        value: authProfile.data.Afdeling.Nummer,
                    },
                ]

                setOptions(d)

                _setDepartmentNumber(authProfile.data.Afdeling.Nummer)
            } else {
                const depResponse = await getDepartments()
                const opt: ISelectOptions[] = depResponse.map(
                    (department: Department) => {
                        return { label: department.name, value: department.id }
                    }
                )

                setOptions(opt)
            }

            return true
        }

        getDeps()
    }, [])

    const chooseImitator = async (member: IMemberSuggestion): Promise<void> => {
        const response = await getProfileByIdent(member.memberId)
        const profile = Profile.build(response.data)

        authProfile.apiProfile = profile

        localStorage.setItem('profile_data', JSON.stringify(authProfile))
        localStorage.setItem('imitator', JSON.stringify(member))

        const hasAccessToCaseResponse = await hasAccessToCase()
        _setCanAccessLoenindplaceringer(hasAccessToCaseResponse.data)
        setAuthRoles([member.role])
        _set(authProfile)
        _setIsAuthenticated(true)
    }

    return (
        <div className={`${layout.container} main-content`}>
            <div className={`${classes.headerArea} desktop-only`} />
            <div className={classes.headerArea}>
                <div>
                    <Typography tag="h1" size="XXXXL">
                        Søg TR & FTR
                    </Typography>
                </div>
            </div>

            <div className={classes.formArea}>
                <FormControl>
                    <Select
                        placeholder="Vælg afdeling *"
                        onChange={(e) => {
                            _setSuggestions([])
                            _setName('')
                            _setDepartmentNumber(e.target.value)
                        }}
                        options={options}
                    />
                </FormControl>
                <FormControl>
                    <Input
                        label="Navn"
                        name="fuldeNavn"
                        required
                        value={name}
                        disabled={!departmentNumber}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                if (name && name !== '' && departmentNumber) {
                                    search(departmentNumber, name)
                                } else {
                                    _setSuggestions([])
                                }
                            }
                        }}
                        onChange={(e) => _setName(e.target.value)}
                    />
                </FormControl>
                <Button
                    style={{ height: '46px' }}
                    buttonType="submit"
                    type="primary"
                    size="large"
                    disabled={!departmentNumber}
                    onClick={() => {
                        if (name && name !== '' && departmentNumber) {
                            search(departmentNumber, name)
                        } else {
                            _setSuggestions([])
                        }
                    }}
                >
                    {L('ALLEMEDLEMMER_FORM_SØG_KNAP')}
                </Button>
            </div>

            <hr className={classes.hr} />

            {loading ? <Spinner /> : null}

            {!suggestions.length ? (
                <Typography
                    size="S"
                    align="center"
                    style={{ marginTop: '20px' }}
                >
                    Vælg afdeling og navn
                </Typography>
            ) : null}

            <div className={classes.members}>
                {suggestions && suggestions.length ? (
                    <div className="page-up">
                        <CardList>
                            <CardHeader
                                gridTemplateColumns={gridTemplateColumns}
                            >
                                <CardHeaderCell />
                                <CardHeaderCell>
                                    <SortButton
                                        onClick={() => setSort('name')}
                                        sort={sortState}
                                        name="name"
                                    >
                                        Navn
                                    </SortButton>
                                </CardHeaderCell>
                                <CardHeaderCell>
                                    <Typography size="XS" weight="bold">
                                        <SortButton
                                            onClick={() =>
                                                setSort('arbejdssted')
                                            }
                                            sort={sortState}
                                            name="arbejdssted"
                                        >
                                            Hverv
                                        </SortButton>
                                    </Typography>
                                </CardHeaderCell>
                                <CardHeaderCell>
                                    <div className={classes.sortPlaceholder}>
                                        Rolle
                                    </div>
                                </CardHeaderCell>
                                <CardHeaderCell>
                                    <SortButton
                                        onClick={() => setSort('department')}
                                        sort={sortState}
                                        name="department"
                                    >
                                        Fødselsdato
                                    </SortButton>
                                </CardHeaderCell>
                                <CardHeaderCell />
                            </CardHeader>
                            {suggestions.map((member) => (
                                <>
                                    <Card
                                        key={member.memberId}
                                        gridTemplateColumns={
                                            gridTemplateColumns
                                        }
                                    >
                                        <CardCell>
                                            <Circle>
                                                <CircleText>
                                                    {getInitials(
                                                        member.fullName
                                                    )}
                                                </CircleText>
                                            </Circle>
                                        </CardCell>
                                        <CardCell>
                                            <Typography size="XS">
                                                {member.fullName}
                                            </Typography>
                                        </CardCell>
                                        <CardCell>
                                            <Typography size="XS">
                                                {member.position}
                                            </Typography>
                                        </CardCell>
                                        <CardCell>
                                            <Typography size="XS">
                                                {member.role}
                                            </Typography>
                                        </CardCell>
                                        <CardCell>
                                            <Typography size="XS">
                                                {member.birthDay}
                                            </Typography>
                                        </CardCell>

                                        <CardCell>
                                            <Button
                                                onClick={() =>
                                                    chooseImitator(member)
                                                }
                                            >
                                                Vælg
                                            </Button>
                                        </CardCell>
                                    </Card>
                                </>
                            ))}
                        </CardList>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export { Imitator }
